import React, { useState, useEffect } from "react";
import { Head, router } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateEST } from "@/lib/utils";

interface SeedEmail {
  id: number;
  email: string;
}

interface EmailRecord {
  id: number;
  subject: string;
  sender: string;
  landed_folder: string;
  received_at: string;
  seed_email: SeedEmail;
  body?: string;
}

interface Domain {
  id: number;
  name: string;
  enabled: boolean;
}

interface Props {
  domains: Domain[];
  groupedRecords: Record<string, EmailRecord[]>;
  selectedDomainName?: string;
  selectedDateFilter?: string;
}

export default function Index({
  domains,
  groupedRecords,
  selectedDomainName,
  selectedDateFilter,
}: Props) {
  const [filter, setFilter] = useState(selectedDomainName || "all");
  const [dateFilter, setDateFilter] = useState(selectedDateFilter || "last_week");
  const [selectedEmail, setSelectedEmail] = useState<EmailRecord | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      router.reload({
        only: ["domains", "groupedRecords"],
        preserveState: true,
        preserveScroll: true,
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
    router.get(
      "/",
      {
        domain_name: filter === "all" ? "" : filter,
        date_filter: value,
      },
      {
        preserveState: false,
        preserveScroll: true,
      }
    );
  };

  const handleDomainFilterChange = (value: string) => {
    setFilter(value);
    router.get(
      "/",
      {
        domain_name: value === "all" ? "" : value,
        date_filter: dateFilter,
      },
      {
        preserveState: false,
        preserveScroll: true,
      }
    );
  };

  const filteredDomains =
    filter !== "all"
      ? domains.filter((d) => d.name === filter)
      : domains;
  const displayRecords =
    filter !== "all"
      ? {
          [filter]: groupedRecords[filter] || [],
        }
      : groupedRecords;

  return (
    <>
      <Head title="Dashboard" />

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Email Tracking Dashboard
            </h1>
            <p className="text-gray-600">
              Monitor where your emails land across different inbox providers
            </p>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex flex-wrap gap-3">
                <Select
                  value={dateFilter}
                  onValueChange={handleDateFilterChange}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select time range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last_week">Last 7 Days</SelectItem>
                    <SelectItem value="last_month">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filter} onValueChange={handleDomainFilterChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select domain" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Domains</SelectItem>
                    {domains.map((domain) => (
                      <SelectItem key={domain.id} value={domain.name}>
                        {domain.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(dateFilter !== "last_week" || filter !== "all") && (
                  <button
                    onClick={() => {
                      setDateFilter("last_week");
                      setFilter("all");
                      router.get(
                        "/",
                        {
                          domain_name: "",
                          date_filter: "last_week",
                        },
                        {
                          preserveState: false,
                          preserveScroll: true,
                        }
                      );
                    }}
                    className="inline-flex items-center gap-2 px-3 h-9 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors cursor-pointer"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Clear filters
                  </button>
                )}
              </div>
              <a
                href="/domains"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-b from-blue-500 to-blue-600 text-white text-sm font-medium rounded-sm transition-colors"
              >
                Manage Domains
              </a>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {Object.keys(displayRecords).length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <svg
                      className="h-6 w-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No email data yet
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                    Add some domains to start tracking emails. The system checks
                    for emails every 5 minutes.
                  </p>
                  <a
                    href="/domains"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Your First Domain
                  </a>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(displayRecords).map(([domainName, records]) => (
                  <div
                    key={domainName}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                  >
                    <div className="px-6 py-4 border-b border-gray-100">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {domainName}
                      </h2>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {records?.length === 1
                          ? "1 email tracked"
                          : `${records?.length || 0} emails tracked`}
                      </p>
                    </div>

                    {records && records.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Seed Email
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Landed In
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Subject Line
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Date & Time
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {records.map((record, index) => (
                              <tr
                                key={record.id}
                                className="hover:bg-gray-50/50 transition-colors duration-150"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                                      <svg
                                        className="h-4 w-4 text-gray-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                        />
                                      </svg>
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">
                                      {record.seed_email?.email}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center justify-center min-w-[90px] px-2.5 py-1 text-xs font-medium rounded-md ${
                                      record.landed_folder === "Inbox" ||
                                      record.landed_folder === "Primary"
                                        ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
                                        : record.landed_folder === "Spam"
                                        ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20"
                                        : record.landed_folder === "Promotions"
                                        ? "bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20"
                                        : "bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20"
                                    }`}
                                  >
                                    <span
                                      className={`mr-1.5 h-1.5 w-1.5 rounded-full ${
                                        record.landed_folder === "Inbox" ||
                                        record.landed_folder === "Primary"
                                          ? "bg-green-600"
                                          : record.landed_folder === "Spam"
                                          ? "bg-red-600"
                                          : record.landed_folder ===
                                            "Promotions"
                                          ? "bg-yellow-600"
                                          : "bg-gray-600"
                                      }`}
                                    ></span>
                                    {record.landed_folder}
                                  </span>
                                </td>
                                <td className="px-6 py-4 max-w-md font-medium">
                                  <div
                                    className="text-sm text-gray-700 truncate"
                                    title={record.subject}
                                  >
                                    {record.subject}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">
                                    {formatDateEST(record.received_at)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <button
                                    onClick={() => setSelectedEmail(record)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors cursor-pointer"
                                  >
                                    Preview
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-3">
                          <svg
                            className="h-6 w-6 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                          </svg>
                        </div>
                        <p className="text-gray-500">
                          No emails found for this domain yet.
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          Emails will appear here as they are received.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={!!selectedEmail}
        onOpenChange={() => setSelectedEmail(null)}
      >
        <DialogContent className="!max-w-[90vw] sm:!max-w-[80vw] lg:!max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>From: {selectedEmail?.sender}</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <h3 className="font-semibold mb-2">
              Subject: {selectedEmail?.subject}
            </h3>
            <div className="text-sm text-gray-500 mb-4">
              Received:{" "}
              {selectedEmail ? formatDateEST(selectedEmail.received_at) : ""}
            </div>
            <div className="border-t pt-4">
              {selectedEmail?.body ? (
                <div
                  className="email-content"
                  style={{
                    maxWidth: "100%",
                    overflowX: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                />
              ) : (
                <p className="text-gray-500">No email body available</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
